import {AboutDialogItem, AppOptionSpec, HoistAppModel, InitContext, managed, XH} from '@xh/hoist/core';
import {TabContainerModel} from '@xh/hoist/cmp/tab';
import {ViewManagerModel} from '@xh/hoist/cmp/viewmanager';
import {Icon} from '@xh/hoist/icon';
import {secmasterTab} from './tabs/secmaster/SecMasterTab';
import {SecMasterService} from './svc/SecMasterService';
import {LivePositionsService} from './svc/LivePositionsService';
import {IntradayService} from './svc/IntradayService';
import {MessageHub} from './svc/MessageHub';
import {NewsService} from './svc/NewsService';
import {WatchListService} from './svc/WatchListService';
import {ChartService} from './svc/ChartService';
import {chartsTab} from './tabs/charts/ChartsTab';
import {action, observable} from '@xh/hoist/mobx';
import {researchTab} from './tabs/research/ResearchTab';
import {ResearchService} from './svc/ResearchService';
import {portfolioTab} from './tabs/portfolio/PortfolioTab';
import {workflowsTab} from './tabs/worflows/WorkflowsTab';
import {WorkflowService} from './svc/WorkflowService';
import {ExchangeService} from './svc/ExchangeService';
import {PerformanceService} from './svc/PerformanceService';
import {accountStructureTab} from './tabs/accountStructure/AccountStructureTab';
import {PortfolioService} from './svc/PortfolioService';
import {themeAppOption} from '@xh/hoist/desktop/cmp/appOption';
import {radioCardInput} from '@xh/hoist/desktop/cmp/input';
import {wsIndicator} from './cmp/wsIndicator/WsIndicator';
import {navModePreview, tickerTapePreview, uiStylePreview} from './cmp/appOption/AppOptionPreviews';
import {registerServiceWorkerAsync} from '../core/PwaUtils';
import {realEstateTab} from './tabs/realEstate/RealEstateTab';
import {Roles} from './data/DataTypes';
import {EntitlementService} from './svc/EntitlementService';
import {OrderService} from './svc/OrderService';
import {performanceTab} from './tabs/performance/PerformanceTab';
import {marketsTab} from './tabs/markets/MarketsTab';
import {AiChatService} from './svc/AiChatService';
import {AiSpecAssistService} from './svc/AiSpecAssistService';
import {AiChatModel} from './cmp/aiChat/AiChatModel';
import {houseBuildingIcon, slidersIcon} from '../core/Icons';
import {applyUiStyleClass} from './cmp/UiStyle';

export class RzxAppModel extends HoistAppModel {
    @managed tabContainerModel;
    @managed runtimeState: RuntimeState = new RuntimeState();
    @managed aiChatModel: AiChatModel;
    /** Saved Research dashboards - see ResearchTabModel. */
    @managed researchViewManagerModel: ViewManagerModel;

    override async initAsync(ctx: InitContext) {
        if (!XH.isDevelopmentMode) await registerServiceWorkerAsync();

        applyUiStyleClass();

        await XH.installServicesAsync([EntitlementService], ctx);
        await XH.installServicesAsync([MessageHub], ctx);
        await XH.installServicesAsync([WatchListService], ctx);
        await XH.installServicesAsync([PortfolioService], ctx);

        await XH.installServicesAsync(
            [
                LivePositionsService,
                NewsService,
                PerformanceService,
                ChartService,
                ResearchService,
                ExchangeService,
                SecMasterService,
                OrderService,
                AiChatService,
                AiSpecAssistService
            ],
            ctx
        );
        // Reads the live positions for the tickers to load
        await XH.installServicesAsync([IntradayService], ctx);

        this.aiChatModel = new AiChatModel();

        // Created here, ahead of the Research tab, so its dashboard has the saved state when it is first built
        this.researchViewManagerModel = await ViewManagerModel.createAsync({
            type: 'researchDashboard',
            typeDisplayName: 'dashboard'
        });

        if (XH.entitlementService.hasRole(Roles.WORKFLOW_MANAGER)) await XH.installServicesAsync([WorkflowService], ctx);

        this.tabContainerModel = new TabContainerModel({
            route: 'default',
            track: true,
            tabs: [
                {
                    id: 'performance',
                    title: 'Performance',
                    icon: Icon.gauge(),
                    content: performanceTab,
                    tooltip: 'Performance Dashboard'
                },
                {
                    id: 'markets',
                    title: 'Markets',
                    icon: Icon.globeAmericas(),
                    content: marketsTab,
                    tooltip: 'Markets Summary'
                },
                {
                    id: 'accountStructure',
                    icon: Icon.treeGraph({prefix: 'fal'}),
                    content: accountStructureTab,
                    tooltip: 'Account Structure'
                },
                {
                    id: 'portfolios',
                    icon: Icon.portfolio(),
                    content: portfolioTab,
                    tooltip: 'Portfolios'
                },
                {
                    id: 'charts',
                    icon: Icon.chartArea(),
                    content: chartsTab,
                    tooltip: 'Charts'
                },
                {
                    id: 'research',
                    icon: Icon.search(),
                    content: researchTab,
                    tooltip: 'Research'
                },
                {
                    id: 'secmaster',
                    icon: slidersIcon(),
                    content: secmasterTab,
                    tooltip: 'Sec Master'
                },
                {
                    id: 'realEstate',
                    icon: houseBuildingIcon(),
                    content: realEstateTab,
                    tooltip: 'Real Estate'
                },
                {
                    id: 'workflows',
                    icon: Icon.gears(),
                    content: workflowsTab,
                    tooltip: 'Workflows',
                    omit: !XH.entitlementService.hasRole(Roles.WORKFLOW_MANAGER)
                }
            ]
        });
    }

    override getRoutes() {
        return [
            {
                name: 'default',
                path: '/rzx',
                children: [
                    {name: 'performance', path: '/performance'},
                    {name: 'markets', path: '/markets'},
                    {
                        name: 'accountStructure',
                        path: '/accountStructure'
                    },
                    {
                        name: 'portfolios',
                        path: '/portfolios',
                        children: [
                            {name: 'positions', path: '/positions'},
                            {name: 'orders', path: '/orders'},
                            {name: 'trades', path: '/trades'}
                        ]
                    },
                    {name: 'charts', path: '/charts'},
                    {name: 'research', path: '/research'},
                    {
                        name: 'secmaster',
                        path: '/secmaster',
                        children: [{name: 'equities', path: '/equities'}]
                    },
                    {
                        name: 'realEstate',
                        path: '/realEstate',
                        children: [
                            {name: 'deals', path: '/deals'},
                            {name: 'allocations', path: '/allocations'},
                            {name: 'distributions', path: '/distributions'}
                        ]
                    },
                    {name: 'workflows', path: '/workflows'}
                ]
            }
        ];
    }

    override getAppOptions(): AppOptionSpec[] {
        return [
            themeAppOption(),
            {
                name: 'showTickerTape',
                prefName: 'showTickerTape',
                formField: {
                    label: 'Show Ticker Tape',
                    info: 'Show/hide scrolling ticker tape tied to one of the custom or default watch lists',
                    item: radioCardInput({
                        options: [
                            {value: false, label: 'Hidden', preview: tickerTapePreview(false)},
                            {value: true, label: 'Shown', preview: tickerTapePreview(true)}
                        ]
                    })
                },
                reloadRequired: true
            },
            {
                name: 'navMode',
                prefName: 'navMode',
                formField: {
                    label: 'Navigation Mode',
                    item: radioCardInput({
                        options: [
                            {value: 'tabs', label: 'Tabs', preview: navModePreview('tabs')},
                            {value: 'sideNavBar', label: 'Side Bar', preview: navModePreview('sideNavBar')}
                        ]
                    })
                },
                reloadRequired: true
            },
            // Offered only once the `uiStyle` pref exists on the server - see UiStyle.ts
            ...(XH.prefService.hasKey('uiStyle') ? [this.uiStyleAppOption()] : [])
        ];
    }

    private uiStyleAppOption(): AppOptionSpec {
        return {
            name: 'uiStyle',
            prefName: 'uiStyle',
            formField: {
                label: 'Display Style',
                info: 'Terminal shows solid colored cells and dense quote grids. Standard uses colored text only.',
                item: radioCardInput({
                    options: [
                        {value: 'standard', label: 'Standard', preview: uiStylePreview('standard')},
                        {value: 'terminal', label: 'Terminal', preview: uiStylePreview('terminal')}
                    ]
                })
            },
            reloadRequired: true
        };
    }

    override getAboutDialogItems(): AboutDialogItem[] {
        const buildIns = super.getAboutDialogItems();
        const wsItem = buildIns.find(di => di.label === 'WebSockets');
        wsItem.label = 'Hoist WebSockets';
        wsItem.value = wsIndicator({showDescription: true, status: 'CON'});

        return [
            ...buildIns,
            {
                label: 'RZX WebSockets',
                value: wsIndicator({showDescription: true})
            }
        ];
    }
}

class RuntimeState extends HoistAppModel {
    @observable accessor chartTabTicker: string;

    @action
    setChartTabTicker(ticker: string) {
        this.chartTabTicker = ticker;
    }
}
